/**
 * API Client Service
 *
 * Centralized HTTP client with:
 * - Automatic JWT token attachment
 * - Token refresh logic
 * - Error handling and retry
 * - Request/response logging
 * - Type-safe methods
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
} from "axios";
import type { APIResponse, APIError } from "@/types";

class APIClient {
  private client: AxiosInstance;
  private refreshing: boolean = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    // Use HTTPS for production, HTTP only for localhost
    const defaultBaseURL =
      window.location.hostname === "localhost"
        ? "http://localhost:8080"
        : "https://localhost:8080";

    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || defaultBaseURL,
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor: Add JWT token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add request ID for tracking
        config.headers["X-Request-ID"] = this.generateRequestId();

        // Log request in development
        if (import.meta.env.DEV) {
          console.log(
            `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
            {
              headers: config.headers,
              data: config.data,
            },
          );
        }

        return config;
      },
      (error) => {
        console.error("[API Request Error]", error);
        return Promise.reject(error);
      },
    );

    // Response interceptor: Handle errors and refresh token
    this.client.interceptors.response.use(
      (response) => {
        // Log response in development
        if (import.meta.env.DEV) {
          console.log(
            `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
            {
              status: response.status,
              data: response.data,
            },
          );
        }

        return response;
      },
      async (error: AxiosError<APIError>) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        // Log error in development
        if (import.meta.env.DEV) {
          console.error("[API Response Error]", {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
          });
        }

        // Handle 401 Unauthorized - attempt token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.refreshing) {
            // Wait for token refresh
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(this.client(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.refreshing = true;

          try {
            const newToken = await this.refreshToken();
            this.refreshing = false;
            this.onRefreshSuccess(newToken);

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }

            return this.client(originalRequest);
          } catch (refreshError) {
            this.refreshing = false;
            this.onRefreshFailure();
            return Promise.reject(refreshError);
          }
        }

        // Handle other errors
        return Promise.reject(this.normalizeError(error));
      },
    );
  }

  private getToken(): string | null {
    return localStorage.getItem("auth_token");
  }

  private setToken(token: string): void {
    localStorage.setItem("auth_token", token);
  }

  private clearToken(): void {
    localStorage.removeItem("auth_token");
  }

  private async refreshToken(): Promise<string> {
    try {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/auth/refresh`,
        { refresh_token: refreshToken },
      );

      const newToken = response.data.access_token;
      this.setToken(newToken);
      return newToken;
    } catch (error) {
      this.clearToken();
      throw error;
    }
  }

  private onRefreshSuccess(token: string): void {
    this.refreshSubscribers.forEach((callback) => callback(token));
    this.refreshSubscribers = [];
  }

  private onRefreshFailure(): void {
    this.refreshSubscribers = [];
    this.clearToken();
    // Redirect to login
    window.location.href = "/login";
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private normalizeError(error: AxiosError<APIError>): APIError {
    if (error.response?.data) {
      return error.response.data;
    }

    return {
      error: error.name || "NetworkError",
      message: error.message || "An unexpected error occurred",
      code: "NETWORK_ERROR",
      status: error.response?.status || 500,
    };
  }

  // ============================================
  // Public API Methods
  // ============================================

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<APIResponse<T>> = await this.client.get(
      url,
      config,
    );
    return response.data.data;
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response: AxiosResponse<APIResponse<T>> = await this.client.post(
      url,
      data,
      config,
    );
    return response.data.data;
  }

  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response: AxiosResponse<APIResponse<T>> = await this.client.put(
      url,
      data,
      config,
    );
    return response.data.data;
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response: AxiosResponse<APIResponse<T>> = await this.client.patch(
      url,
      data,
      config,
    );
    return response.data.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<APIResponse<T>> = await this.client.delete(
      url,
      config,
    );
    return response.data.data;
  }

  // Raw methods (without unwrapping data)
  async getRaw<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.client.get(url, config);
  }

  async postRaw<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.client.post(url, data, config);
  }
}

// Export singleton instance
export const apiClient = new APIClient();
export default apiClient;
